/**
 * Cleanup and retention management for tracing system
 * Prevents memory leaks and manages disk space
 */

import { TraceAnalysis, ConversationTrace } from './trace-types';
import { unlink, readdir, stat } from 'fs/promises';
import { join } from 'path';

export interface RetentionPolicy {
  // In-memory cleanup
  maxInMemoryTraces: number;
  maxInMemoryAgeDays: number;
  
  // Disk cleanup
  maxDiskTraces: number;
  maxDiskAgeDays: number;
  maxDiskSizeGB: number;
  
  // Cleanup intervals
  cleanupIntervalMinutes: number;
  
  // What to preserve
  preserveFailedTraces: boolean;
  preserveRecentAnalyses: number;
}

export const DEFAULT_RETENTION_POLICY: RetentionPolicy = {
  maxInMemoryTraces: 1000,
  maxInMemoryAgeDays: 1,
  maxDiskTraces: 10000,
  maxDiskAgeDays: 30,
  maxDiskSizeGB: 1,
  cleanupIntervalMinutes: 15,
  preserveFailedTraces: true,
  preserveRecentAnalyses: 10
};

export class TraceCleanupManager {
  private cleanupTimer: NodeJS.Timeout | null = null;
  private tracePath: string;
  private policy: RetentionPolicy;
  private lastCleanup: Date | null = null;
  
  constructor(tracePath: string, policy: RetentionPolicy = DEFAULT_RETENTION_POLICY) {
    this.tracePath = tracePath;
    this.policy = { ...DEFAULT_RETENTION_POLICY, ...policy };
  }
  
  /**
   * Start automatic cleanup timer
   */
  startAutoCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    const intervalMs = this.policy.cleanupIntervalMinutes * 60 * 1000;
    this.cleanupTimer = setInterval(() => {
      this.performCleanup().catch(error => {
        console.warn('Automatic cleanup failed:', error);
      });
    }, intervalMs);
    
    console.log(`🧹 Trace cleanup scheduled every ${this.policy.cleanupIntervalMinutes} minutes`);
  }
  
  /**
   * Stop automatic cleanup
   */
  stopAutoCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }
  
  /**
   * Perform comprehensive cleanup
   */
  async performCleanup(): Promise<{
    memoryCleanup: { before: number; after: number; removed: number };
    diskCleanup: { filesRemoved: number; bytesFreed: number };
    errors: string[];
  }> {
    const startTime = Date.now();
    const result = {
      memoryCleanup: { before: 0, after: 0, removed: 0 },
      diskCleanup: { filesRemoved: 0, bytesFreed: 0 },
      errors: [] as string[]
    };
    
    console.log('🧹 Starting trace cleanup...');
    
    try {
      // Clean up disk traces
      const diskCleanup = await this.cleanupDiskTraces();
      result.diskCleanup = diskCleanup;
      
    } catch (error) {
      const errorMsg = `Cleanup failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      result.errors.push(errorMsg);
      console.error('❌', errorMsg);
    }
    
    this.lastCleanup = new Date();
    const duration = Date.now() - startTime;
    
    console.log('✅ Cleanup completed:', {
      duration: duration + 'ms',
      diskFilesRemoved: result.diskCleanup.filesRemoved,
      memoryTracesRemoved: result.memoryCleanup.removed,
      errors: result.errors.length
    });
    
    return result;
  }
  
  /**
   * Clean up in-memory traces (to be called by tracer)
   */
  cleanupInMemoryTraces(traces: Map<string, ConversationTrace>): {
    before: number;
    after: number; 
    removed: number;
  } {
    const before = traces.size;
    
    if (before === 0) {
      return { before: 0, after: 0, removed: 0 };
    }
    
    const now = new Date();
    const maxAgeMs = this.policy.maxInMemoryAgeDays * 24 * 60 * 60 * 1000;
    const cutoffTime = new Date(now.getTime() - maxAgeMs);
    
    // Convert to array for sorting and filtering
    const traceArray = Array.from(traces.entries());
    
    // Sort by timestamp (newest first)
    traceArray.sort((a, b) => 
      b[1].context.timestamp.getTime() - a[1].context.timestamp.getTime()
    );
    
    // Determine what to keep
    const tracesToKeep: [string, ConversationTrace][] = [];
    
    for (let i = 0; i < traceArray.length; i++) {
      const [traceId, trace] = traceArray[i];
      const shouldKeep = (
        // Keep if under max count limit
        i < this.policy.maxInMemoryTraces &&
        // Keep if not too old
        trace.context.timestamp >= cutoffTime
      ) || (
        // Always keep failed traces if policy says so
        this.policy.preserveFailedTraces && !trace.flow.operationTrace.result.success
      );
      
      if (shouldKeep) {
        tracesToKeep.push([traceId, trace]);
      }
    }
    
    // Clear the map and repopulate with traces to keep
    traces.clear();
    tracesToKeep.forEach(([id, trace]) => {
      traces.set(id, trace);
    });
    
    const after = traces.size;
    const removed = before - after;
    
    if (removed > 0) {
      console.log(`🧹 In-memory cleanup: ${removed} traces removed (${before} → ${after})`);
    }
    
    return { before, after, removed };
  }
  
  /**
   * Clean up disk trace files
   */
  private async cleanupDiskTraces(): Promise<{
    filesRemoved: number;
    bytesFreed: number;
  }> {
    const result = { filesRemoved: 0, bytesFreed: 0 };
    
    try {
      const files = await readdir(this.tracePath);
      const traceFiles = files.filter(f => f.endsWith('.json') && !f.startsWith('analysis_'));
      
      if (traceFiles.length === 0) {
        return result;
      }
      
      // Get file stats
      const fileStats = await Promise.all(
        traceFiles.map(async filename => {
          try {
            const filepath = join(this.tracePath, filename);
            const stats = await stat(filepath);
            return {
              filename,
              filepath,
              size: stats.size,
              mtime: stats.mtime
            };
          } catch (error) {
            return null;
          }
        })
      );
      
      const validFiles = fileStats.filter(Boolean);
      
      // Sort by modification time (newest first)
      validFiles.sort((a, b) => {
        if (!a || !b) return 0;
        return b.mtime.getTime() - a.mtime.getTime();
      });
      
      // Calculate what to remove
      const now = new Date();
      const maxAgeMs = this.policy.maxDiskAgeDays * 24 * 60 * 60 * 1000;
      const cutoffTime = new Date(now.getTime() - maxAgeMs);
      const maxSizeBytes = this.policy.maxDiskSizeGB * 1024 * 1024 * 1024;
      
      let totalSize = 0;
      const filesToRemove = [];
      
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        if (!file) continue;
        
        const shouldRemove = (
          // Remove if over count limit
          i >= this.policy.maxDiskTraces ||
          // Remove if too old
          file.mtime < cutoffTime ||
          // Remove if total size would exceed limit
          (totalSize + file.size > maxSizeBytes && i > 10) // Keep at least 10 recent
        );
        
        if (shouldRemove) {
          filesToRemove.push(file);
        } else {
          totalSize += file.size;
        }
      }
      
      // Remove files
      for (const file of filesToRemove) {
        if (!file) continue;
        try {
          await unlink(file.filepath);
          result.filesRemoved++;
          result.bytesFreed += file.size;
        } catch (error) {
          console.warn(`Failed to remove trace file ${file.filename}:`, error);
        }
      }
      
      if (result.filesRemoved > 0) {
        console.log(`🧹 Disk cleanup: ${result.filesRemoved} files removed, ${Math.round(result.bytesFreed / 1024 / 1024)}MB freed`);
      }
      
    } catch (error) {
      console.warn('Disk cleanup failed:', error);
    }
    
    return result;
  }
  
  /**
   * Get cleanup statistics
   */
  getCleanupStats(): {
    policy: RetentionPolicy;
    lastCleanup: Date | null;
    isAutoCleanupRunning: boolean;
    nextCleanupETA: Date | null;
  } {
    const nextCleanupETA = this.lastCleanup && this.cleanupTimer
      ? new Date(this.lastCleanup.getTime() + (this.policy.cleanupIntervalMinutes * 60 * 1000))
      : null;
      
    return {
      policy: this.policy,
      lastCleanup: this.lastCleanup,
      isAutoCleanupRunning: !!this.cleanupTimer,
      nextCleanupETA
    };
  }
  
  /**
   * Update retention policy
   */
  updatePolicy(newPolicy: Partial<RetentionPolicy>): void {
    this.policy = { ...this.policy, ...newPolicy };
    
    // Restart auto cleanup with new interval if it was running
    if (this.cleanupTimer) {
      this.stopAutoCleanup();
      this.startAutoCleanup();
    }
    
    console.log('🔧 Retention policy updated:', this.policy);
  }
  
  /**
   * Force immediate cleanup
   */
  async forceCleanup(): Promise<void> {
    await this.performCleanup();
  }
}